import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PokerOddsCalculator = () => {
  const [userHand, setUserHand] = useState(['', '']);
  const [opponentHand, setOpponentHand] = useState(['', '']);
  const [odds, setOdds] = useState(null);
  const [error, setError] = useState('');
  const [showMore, setShowMore] = useState(false);

  const suits = ['♠', '♥', '♦', '♣'];
  const ranks = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

  const handleUserHandChange = (index, value) => {
    const newHand = [...userHand];
    newHand[index] = value;
    setUserHand(newHand);
  };

  const handleOpponentHandChange = (index, value) => {
    const newHand = [...opponentHand];
    newHand[index] = value;
    setOpponentHand(newHand);
  };

  const calculateOdds = () => {
    // Validate user input
    if (!userHand.every(card => card) || !opponentHand.every(card => card)) {
      setError('Please enter all cards.');
      return;
    }

    if (new Set([...userHand, ...opponentHand]).size !== 4) {
      setError('Cards must be unique.');
      return;
    }

    // Calculate odds (simplified for demonstration)
    const userRank = ranks.indexOf(userHand[0][0]) + ranks.indexOf(userHand[1][0]);
    const opponentRank = ranks.indexOf(opponentHand[0][0]) + ranks.indexOf(opponentHand[1][0]);

    const userOdds = userRank > opponentRank ? 60 : 40;
    const opponentOdds = 100 - userOdds;

    setOdds({ userOdds, opponentOdds });
    setError('');
  };

  useEffect(() => {
    setOdds(null);
  }, [userHand, opponentHand]);

  const chartData = {
    labels: ['Your Hand', "Opponent's Hand"],
    datasets: [
      {
        label: 'Odds of Winning',
        data: odds ? [odds.userOdds, odds.opponentOdds] : [0, 0],
        backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(255, 99, 132, 0.5)'],
        borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Poker Odds Calculator</title>
        <meta name="description" content="Calculate the odds of winning for your poker hand against a random opponent's hand." />
        <meta name="keywords" content="Poker Odds Calculator, Texas Hold'em, Poker Hands, Odds" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-green-600 mr-4">🃏</div>
            <h1 className="text-3xl font-bold text-gray-800">Poker Odds Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Enter your hand and your opponent's hand to calculate the odds of winning.</p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Hand:</label>
                <div className="flex space-x-4">
                  <select
                    value={userHand[0]}
                    onChange={(e) => handleUserHandChange(0, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select Card</option>
                    {ranks.flatMap(rank => suits.map(suit => (
                      <option key={`${rank}${suit}`} value={`${rank}${suit}`}>{rank}{suit}</option>
                    )))}
                  </select>
                  <select
                    value={userHand[1]}
                    onChange={(e) => handleUserHandChange(1, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select Card</option>
                    {ranks.flatMap(rank => suits.map(suit => (
                      <option key={`${rank}${suit}`} value={`${rank}${suit}`}>{rank}{suit}</option>
                    )))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opponent's Hand:</label>
                <div className="flex space-x-4">
                  <select
                    value={opponentHand[0]}
                    onChange={(e) => handleOpponentHandChange(0, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select Card</option>
                    {ranks.flatMap(rank => suits.map(suit => (
                      <option key={`${rank}${suit}`} value={`${rank}${suit}`}>{rank}{suit}</option>
                    )))}
                  </select>
                  <select
                    value={opponentHand[1]}
                    onChange={(e) => handleOpponentHandChange(1, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select Card</option>
                    {ranks.flatMap(rank => suits.map(suit => (
                      <option key={`${rank}${suit}`} value={`${rank}${suit}`}>{rank}{suit}</option>
                    )))}
                  </select>
                </div>
              </div>
            </div>

            <button
              onClick={calculateOdds}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Odds
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {odds && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Odds of Winning</h2>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-600">Your Hand: {userHand.join(', ')} - {odds.userOdds}%</p>
                  <p className="text-gray-600">Opponent's Hand: {opponentHand.join(', ')} - {odds.opponentOdds}%</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Odds (%)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Hands'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Odds of Winning',
                          font: {
                            size: 16
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Poker Odds Calculator:</p>
            <ol className="list-decimal pl-6 mt-2">
              <li>Select your hand by choosing two cards from the dropdown menus.</li>
              <li>Select your opponent's hand by choosing two cards from the dropdown menus.</li>
              <li>Click the "Calculate Odds" button to see the odds of winning for each hand.</li>
              <li>The odds will be displayed along with a graphical representation.</li>
            </ol>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="text-gray-600">
            <p>Let's say you have a hand of <strong>A♠</strong> and <strong>K♥</strong>, and your opponent has a hand of <strong>Q♦</strong> and <strong>J♣</strong>.</p>
            <p>After entering these cards and clicking "Calculate Odds", you might see odds like:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Your Hand: A♠, K♥ - 60%</li>
              <li>Opponent's Hand: Q♦, J♣ - 40%</li>
            </ul>
            <p>This means that based on the simplified calculation, you have a 60% chance of winning against your opponent's hand.</p>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p><strong>Q: How are the odds calculated?</strong></p>
            <p>A: The odds are calculated based on a simplified ranking system. In a real application, you would use a more sophisticated algorithm or a library to calculate the exact odds.</p>

            <p><strong>Q: Can I use this calculator for other poker variants?</strong></p>
            <p>A: This calculator is designed for Texas Hold'em. For other variants, the logic would need to be adjusted accordingly.</p>

            <p><strong>Q: Why are the odds not exact?</strong></p>
            <p>A: The odds provided are a simplified estimation. For precise odds, consider using a dedicated poker odds calculator or software.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PokerOddsCalculator;